import { useMemo, useCallback, useState, useRef, useEffect } from 'react';
import { AUDITED_TYPE_CONSOLIDATED, AUDITED_TYPE_STANDALONE } from '../../../../utils/constants/commonConstants';
import { deriveAuditedType } from './auditedTypeUtils';

/**
 * Manages consolidated/standalone financial type selection.
 *
 * Lifecycle:
 * 1. Consumer fetches data → API response includes `latestConsolidatedFY` / `latestStandaloneFY` (number | null).
 * 2. Consumer calls `setAuditedTypeFromYears(consolidatedFY, standaloneFY)`.
 *    - On first call (when `auditedType` is still `null`), this derives and sets the initial dropdown value.
 *    - On subsequent calls, only the FY values update (dropdown stays as the user chose).
 *    - Priority: if both non-null and standaloneFY > consolidatedFY → Standalone; otherwise Consolidated.
 * 3. User picks from dropdown → `handleConsolidationChange(val)` updates `auditedType` and calls `onConsolidationChange`.
 * 4. `onConsolidationChange(nature)` fires only on user-driven changes, giving the consumer
 *    the uppercase nature string (e.g. `'CONSOLIDATED'`) to re-fetch data.
 *
 * @param onConsolidationChange — called with the uppercase nature string when the user
 *   explicitly changes the dropdown. Not called on initial load or programmatic updates.
 * @param resetKey — when this value changes (e.g. orgId), the hook resets all state so a
 *   new org's data is not frozen by a previous user selection.
 */

type AuditedTypeOption = { misDisplayName: string; isDataAvailable: boolean };

interface UseAuditedTypeReturn {
    auditedType: string | null;
    latestConsolidatedFY: number | null;
    latestStandaloneFY: number | null;
    auditedTypeOptionsObject: AuditedTypeOption[];
    handleConsolidationChange: (val: string) => void;
    setAuditedTypeFromYears: (consolidatedFY: number | null, standaloneFY: number | null) => void;
}

export function useAuditedType(onConsolidationChange?: (nature: string) => void, resetKey?: string | number): UseAuditedTypeReturn {
    const [auditedType, setAuditedType] = useState<string | null>(null);
    const [latestConsolidatedFY, setLatestConsolidatedFY] = useState<number | null>(null);
    const [latestStandaloneFY, setLatestStandaloneFY] = useState<number | null>(null);
    const [isAuditedTypeUserSelected, setIsAuditedTypeUserSelected] = useState(false);

    // Ref kept in sync with state so the stable callback can read the latest value
    const isAuditedTypeUserSelectedRef = useRef(false);
    useEffect(() => {
        isAuditedTypeUserSelectedRef.current = isAuditedTypeUserSelected;
    }, [isAuditedTypeUserSelected]);

    // Reset all state when the consumer navigates to a different org (or equivalent entity)
    useEffect(() => {
        if (resetKey === undefined) return;
        setAuditedType(null);
        setIsAuditedTypeUserSelected(false);
        setLatestConsolidatedFY(null);
        setLatestStandaloneFY(null);
    }, [resetKey]);

    const auditedTypeOptionsObject = useMemo<AuditedTypeOption[]>(
        () => [
            { misDisplayName: AUDITED_TYPE_CONSOLIDATED, isDataAvailable: latestConsolidatedFY !== null },
            { misDisplayName: AUDITED_TYPE_STANDALONE, isDataAvailable: latestStandaloneFY !== null },
        ],
        [latestConsolidatedFY, latestStandaloneFY],
    );

    /**
     * Called with API-returned FY values. Re-derives the default dropdown value unless
     * the user has already made an explicit choice (`isAuditedTypeUserSelected`).
     * Does NOT trigger `onConsolidationChange`.
     * Priority: both non-null and standaloneFY > consolidatedFY → Standalone; else Consolidated if present; else Standalone if present.
     */
    const setAuditedTypeFromYears = useCallback(
        (consolidatedFY: number | null, standaloneFY: number | null) => {
            setLatestConsolidatedFY(consolidatedFY);
            setLatestStandaloneFY(standaloneFY);
            setAuditedType((prev) => {
                if (isAuditedTypeUserSelectedRef.current) return prev;
                return deriveAuditedType(consolidatedFY, standaloneFY);
            });
        },
        [], // stable reference — reads isAuditedTypeUserSelected via ref
    );

    /**
     * Called when user picks from dropdown. Validates availability, updates state,
     * and fires `onConsolidationChange` with the uppercase nature string.
     */
    const handleConsolidationChange = useCallback(
        (val: string) => {
            setAuditedType((prev) => {
                if (val === prev) return prev;
                const isAvailable = val === AUDITED_TYPE_CONSOLIDATED ? latestConsolidatedFY !== null : latestStandaloneFY !== null;
                if (!isAvailable) return prev;
                onConsolidationChange?.(val.toUpperCase());
                return val;
            });
            setIsAuditedTypeUserSelected(true);
        },
        [latestConsolidatedFY, latestStandaloneFY, onConsolidationChange],
    );

    return {
        auditedType,
        latestConsolidatedFY,
        latestStandaloneFY,
        auditedTypeOptionsObject,
        handleConsolidationChange,
        setAuditedTypeFromYears,
    };
}

/** Convert a display value (e.g. 'Consolidated') to the API nature string ('CONSOLIDATED'). */
export const toNature = (type: string | null): string | undefined => type?.toUpperCase() ?? undefined;
